import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

const SKELETON_SX = { bgcolor: '#EEF1F5', borderRadius: '6px' };

/** Placeholder mirroring a `ListCard` (avatar + two lines + chips). */
export const ListCardSkeleton = ({ withAvatar = true, withChips = true }: { withAvatar?: boolean; withChips?: boolean }) => (
    <Box sx={{ display: 'flex', gap: 1.5, p: 2, mb: 1, borderRadius: '0.5rem', bgcolor: 'background.paper', boxShadow: 1 }}>
        {withAvatar && <Skeleton variant='circular' width={32} height={32} sx={{ bgcolor: SKELETON_SX.bgcolor, flexShrink: 0 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant='rounded' width='55%' height={14} sx={SKELETON_SX} />
            <Skeleton variant='rounded' width='35%' height={11} sx={{ ...SKELETON_SX, mt: 1 }} />
            {withChips && (
                <Stack direction='row' spacing={1} sx={{ mt: 1.5 }}>
                    <Skeleton variant='rounded' width={64} height={20} sx={{ ...SKELETON_SX, borderRadius: '10px' }} />
                    <Skeleton variant='rounded' width={80} height={20} sx={{ ...SKELETON_SX, borderRadius: '10px' }} />
                </Stack>
            )}
        </Box>
    </Box>
);

/** A column of `ListCardSkeleton`, fading out towards the bottom. */
export const ListSkeleton = ({ count = 6, ...cardProps }: { count?: number; withAvatar?: boolean; withChips?: boolean }) => (
    <Box aria-busy='true' aria-label='Chargement' sx={{ maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)' }}>
        {Array.from({ length: count }).map((_, index) => <ListCardSkeleton key={index} {...cardProps} />)}
    </Box>
);

/** Placeholder for a detail panel (card header + form-like rows). */
export const DetailSkeleton = ({ rows = 6 }: { rows?: number }) => (
    <Box aria-busy='true' aria-label='Chargement' sx={{ p: 3 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 4 }}>
            <Skeleton variant='circular' width={40} height={40} sx={{ bgcolor: SKELETON_SX.bgcolor }} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='rounded' width={220} height={22} sx={SKELETON_SX} />
                <Skeleton variant='rounded' width={140} height={12} sx={{ ...SKELETON_SX, mt: 1 }} />
            </Box>
            <Skeleton variant='rounded' width={120} height={36} sx={{ ...SKELETON_SX, borderRadius: '10px' }} />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {Array.from({ length: rows }).map((_, index) => (
                <Box key={index}>
                    <Skeleton variant='rounded' width='40%' height={11} sx={SKELETON_SX} />
                    <Skeleton variant='rounded' height={40} sx={{ ...SKELETON_SX, mt: 1, borderRadius: '8px' }} />
                </Box>
            ))}
        </Box>
    </Box>
);

/** Full-viewport splash shown while the session is being restored. */
export const AppSplash = () => (
    <Box
        aria-busy='true'
        aria-label='Chargement'
        sx={{
            minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
            bgcolor: 'background.default',
            '@keyframes splash-pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.55, transform: 'scale(0.96)' } },
        }}
    >
        <Box
            component='img'
            alt='Chronos'
            src={process.env.PUBLIC_URL + '/icons/squared_logo.svg'}
            sx={{ width: 48, height: 48, animation: 'splash-pulse 1.4s ease-in-out infinite' }}
        />
        <Box sx={{ width: 120, height: 3, borderRadius: 2, bgcolor: '#E3E7EC', overflow: 'hidden', position: 'relative' }}>
            <Box sx={{
                position: 'absolute', inset: 0, width: '40%', borderRadius: 2, bgcolor: 'secondary.main',
                '@keyframes splash-bar': { from: { transform: 'translateX(-100%)' }, to: { transform: 'translateX(250%)' } },
                animation: 'splash-bar 1.2s ease-in-out infinite',
            }} />
        </Box>
    </Box>
);
